import { z } from 'zod';

/**
 * Pre-flight custom rules (Phase 8.4 — `defer-preflight-rule-editor`).
 *
 * Lets operators author per-project bans on top of the built-in
 * pre-flight registry. A rule matches a step when:
 *
 *   - `kind: 'step-type'` + the step's `type` equals `value`
 *     (e.g. ban `db-query` on a customer-facing project).
 *
 *   - `kind: 'selector'` + the step's `target` contains `value`
 *     as a substring (operators ban brittle selectors like
 *     `[data-test="legacy-id"]` without writing regex).
 *
 *   - `kind: 'domain'` + the step is a `navigate` (or carries a URL
 *     target) and the URL host matches `value` (operators ban
 *     `localhost`, `prod.internal`, …).
 *
 * The library treats every match as a single finding with the
 * `category: 'safety'` bucket (so operators can sort by the same
 * column as the built-in safety rules). Severity is operator-set
 * per rule; default `'warn'` so a ban surfaces but doesn't break
 * existing flows out-of-the-blue.
 *
 * Persistence is intentionally pluggable: Phase 4.5 stores ship to
 * sessionStorage, Phase 8 swapped components + approvals to the
 * server, and this row will follow the same path once a user
 * actually asks for it. Today the contract is server-shaped so the
 * eventual swap is a single-file replacement of the store.
 */

export const PreflightCustomRuleKindSchema = z.enum(['step-type', 'selector', 'domain']);
export type PreflightCustomRuleKind = z.infer<typeof PreflightCustomRuleKindSchema>;

export const PreflightCustomRuleSeveritySchema = z.enum(['block', 'warn']);
export type PreflightCustomRuleSeverity = z.infer<typeof PreflightCustomRuleSeveritySchema>;

export const PreflightCustomRuleSchema = z
  .object({
    id: z.string().min(1),
    projectId: z.string().min(1),
    kind: PreflightCustomRuleKindSchema,
    value: z.string().min(1),
    severity: PreflightCustomRuleSeveritySchema.default('warn'),
    reason: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .passthrough();
export type PreflightCustomRule = z.infer<typeof PreflightCustomRuleSchema>;

export const PreflightCustomRuleListResponseSchema = z.object({
  items: z.array(PreflightCustomRuleSchema),
});
export type PreflightCustomRuleListResponse = z.infer<typeof PreflightCustomRuleListResponseSchema>;

export const PreflightCustomRuleUpsertRequestSchema = PreflightCustomRuleSchema;
export type PreflightCustomRuleUpsertRequest = z.infer<typeof PreflightCustomRuleUpsertRequestSchema>;

export const PreflightCustomRuleDeleteResponseSchema = z.object({
  deleted: z.boolean(),
});
export type PreflightCustomRuleDeleteResponse = z.infer<typeof PreflightCustomRuleDeleteResponseSchema>;
